import { useMemo } from 'react'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ReviewButtons } from './ReviewButtons'
import { ApprovedButton } from './ApprovedButton'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { DesignChannel } from 'components/NewFileModal/Types'
import { PostingOnMenu } from '../Toolbar/PostingOnMenu'
import { Approval } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { CalendarFilters } from 'screens/Calendar/types'
import { useScheduledPostTargets } from 'hooks/queries/scheduledPostTargets'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { RejectedButton } from './RejectedButton'
import { ConnectButton } from './ConnectButton'

interface PostApprovalProps {
  almanacFile: AlmanacFileShowItem
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  onDelete: () => void
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  filters: CalendarFilters
  fileChannel: ChannelType | DesignChannel | GenerationType
  startDate?: string
  goToNextFile?: () => void
}

export const PostApproval = ({
  almanacFile,
  isLoading,
  setIsLoading,
  onDelete,
  onUpdateAlmanacFile,
  filters,
  fileChannel,
  startDate,
  goToNextFile,
}: PostApprovalProps) => {
  const workspace = useCurrentWorkspace()
  const { data: targets = [] } = useScheduledPostTargets(almanacFile.scheduled_post?.id, {
    enablePolling: true,
  })
  const { data: externalIntegrations } = useExternalIntegrations()
  const isDocument = almanacFile.asset_type === AssetType.Article
  const integrations = externalIntegrations?.external_integrations ?? []
  const needsToConnectBlogIntegration =
    isDocument &&
    almanacFile.channel === ChannelType.BlogPost &&
    !integrations.some((integration) =>
      [ExternalIntegrationEnum.WordPress, ExternalIntegrationEnum.GoHighLevel, ExternalIntegrationEnum.Wix].includes(
        integration.provider as ExternalIntegrationEnum,
      ),
    )
  const needsToConnectEmailIntegration =
    isDocument &&
    almanacFile.channel === ChannelType.Newsletter &&
    !integrations.some((integration) =>
      [ExternalIntegrationEnum.Mailchimp].includes(integration.provider as ExternalIntegrationEnum),
    )
  const needsToConnectSocialIntegration =
    !isDocument &&
    (targets.length === 0 ||
      !targets.some((target) => integrations.some((integration) => integration.provider === target.provider)))
  const needsToConnectIntegrations =
    needsToConnectBlogIntegration || needsToConnectEmailIntegration || needsToConnectSocialIntegration

  const approvalState = useMemo(() => {
    if (almanacFile.reviewed_at && almanacFile.post_status === PostStatusPropertyValue.AutoPostScheduled) {
      return 'approved'
    } else if (almanacFile.reviewed_at && almanacFile.post_status === PostStatusPropertyValue.Draft) {
      return 'rejected'
    } else if (almanacFile.post_status === PostStatusPropertyValue.Posted) {
      return 'posted'
    } else if (!almanacFile.reviewed_at && almanacFile.post_at) {
      return 'pending'
    }

    return 'draft'
  }, [almanacFile])

  const isPostApprovalsEnabled = workspace?.post_approval_enabled

  if (!isPostApprovalsEnabled || approvalState === 'posted') return null

  return (
    <>
      {approvalState === 'pending' && (
        <ReviewButtons
          almanacFile={almanacFile}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onDelete={onDelete}
          onUpdateAlmanacFile={onUpdateAlmanacFile}
        />
      )}
      {approvalState === 'approved' && !needsToConnectIntegrations && (
        <ApprovedButton
          almanacFile={almanacFile}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onUpdateAlmanacFile={onUpdateAlmanacFile}
          goToNextFile={goToNextFile}
        />
      )}
      {approvalState === 'approved' && needsToConnectIntegrations && <ConnectButton goToNextFile={goToNextFile} />}
      {approvalState === 'rejected' && (
        <RejectedButton
          almanacFile={almanacFile}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onDelete={onDelete}
          filters={filters}
          fileChannel={fileChannel}
          startDate={startDate}
          onUpdateAlmanacFile={onUpdateAlmanacFile}
          goToNextFile={goToNextFile}
        />
      )}
      {approvalState === 'draft' && (
        <PostingOnMenu
          almanacFile={almanacFile}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          onDelete={onDelete}
          filters={filters}
          fileChannel={fileChannel}
          startDate={startDate}
          onUpdateAlmanacFile={onUpdateAlmanacFile}
          reviewAndSchedule
          trigger={
            <Button variant="green" fullWidth frontIcon={Approval} isDisabled={isLoading}>
              Approve & Schedule
            </Button>
          }
        />
      )}
    </>
  )
}
